// Authentication via d3-auth OAuth2 with PKCE

let ACCESS_TOKEN_KEY = 'access_token';
let REFRESH_TOKEN_KEY = 'refresh_token';
// PKCE verifier is stored via the same storage adapter so it survives
// Capacitor's external OAuth roundtrip (sessionStorage doesn't).
let PKCE_VERIFIER_KEY = 'pkce_verifier';
// Auth log key holds a small ring buffer of recent auth events so a token
// refresh failure that happens while the tab is closed is still inspectable
// later. Lives in localStorage directly (not the storage adapter) so it
// survives even when token state is wiped by _clearTokens.
let AUTH_LOG_KEY = 'auth_log';
const AUTH_LOG_MAX_ENTRIES = 50;

/**
 * Append an auth-event entry to the localStorage ring buffer. Best-effort:
 * any failure (storage full, JSON parse error, no localStorage) is swallowed
 * — we never want diagnostic logging to break the auth path.
 * @param {string} event - short event name (e.g. 'refresh_attempt', 'refresh_failed')
 * @param {object} [data] - additional structured context (no PII / no tokens)
 */
function logAuthEvent(event, data) {
    try {
        const raw = localStorage.getItem(AUTH_LOG_KEY);
        const entries = raw ? JSON.parse(raw) : [];
        entries.push({ t: new Date().toISOString(), event, ...(data || {}) });
        if (entries.length > AUTH_LOG_MAX_ENTRIES) {
            entries.splice(0, entries.length - AUTH_LOG_MAX_ENTRIES);
        }
        localStorage.setItem(AUTH_LOG_KEY, JSON.stringify(entries));
    } catch {
        // Logging is diagnostic only — never let it surface.
    }
}

/**
 * Read the auth-event ring buffer. Exposed for in-browser inspection of
 * past refresh attempts after the user has been signed out.
 * @returns {Array<object>}
 */
export function readAuthLog() {
    try {
        const raw = localStorage.getItem(AUTH_LOG_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

/**
 * Async storage adapter interface. The default implementation wraps
 * localStorage. Apps can inject a different adapter via
 * authState.setStorage() to back tokens with Keychain / EncryptedSharedPreferences
 * on native platforms.
 * @typedef {object} AuthStorage
 * @property {(key: string) => Promise<string | null>} get
 * @property {(key: string, value: string) => Promise<void>} set
 * @property {(key: string) => Promise<void>} remove
 */

/** @type {AuthStorage} */
const localStorageAdapter = {
    get: (key) => Promise.resolve(localStorage.getItem(key)),
    set: (key, value) => { localStorage.setItem(key, value); return Promise.resolve(); },
    remove: (key) => { localStorage.removeItem(key); return Promise.resolve(); },
};

/**
 * Decode the payload from a JWT without verification.
 * @param {string} token
 * @returns {object}
 */
function decodeJwtPayload(token) {
    try {
        const payload = token.split('.')[1];
        return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
        return {};
    }
}

function generateRandomString(length) {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}

async function generatePKCE() {
    const verifier = generateRandomString(32);
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    return { verifier, challenge };
}

/**
 * @typedef {object} AuthConfig
 * @property {string} d3AuthBaseURL - d3-auth API base URL (used for token/userinfo/refresh fetches, JWKS, logout redirect)
 * @property {string} d3AuthFrontendURL - d3-auth SPA base URL (used for the /oauth/{tenant}/authorize browser redirect)
 * @property {string} d3AuthClientID - OAuth2 client ID
 * @property {string} d3AuthAudience - OAuth2 audience
 * @property {string} d3AuthTenantSlug - d3-auth tenant slug
 * @property {string} [appName] - App name for storage key prefixing (default: none)
 * @property {string} [loginRedirectUri] - OAuth redirect_uri. Defaults to `${window.location.origin}/auth/callback`. Override for Electron/Capacitor where the WebView origin differs from the claimed OAuth callback URL.
 * @property {string} [logoutReturnTo] - URL d3-auth redirects to after logout. Defaults to `window.location.origin`. Override for Electron/Capacitor — for native apps, use a path claimed by the app's Universal/App Link so the OS hands control back before the browser renders content.
 */

export const authState = {
    isAuthenticated: false,
    loading: true,
    user: null,
    error: null,
    listeners: new Set(),
    _config: null,
    _loginOpener: null,
    _storage: localStorageAdapter,

    notify() {
        this.listeners.forEach(fn => fn(this));
    },

    subscribe(fn) {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    },

    /**
     * Set the storage adapter used for tokens and the PKCE verifier. Call
     * this BEFORE init() so the new adapter is used for all reads.
     * @param {AuthStorage} storage
     */
    setStorage(storage) {
        this._storage = storage;
    },

    /**
     * Initialize auth state. Checks for OAuth callback or existing token.
     * @param {AuthConfig} config
     */
    async init(config) {
        try {
            this.loading = true;
            this._config = config;
            this._loginRedirectUri = config.loginRedirectUri || (window.location.origin + '/auth/callback');
            this._logoutReturnTo = config.logoutReturnTo || window.location.origin;

            // Apply optional app-specific prefix to storage keys
            if (config.appName) {
                const prefix = config.appName + '_';
                ACCESS_TOKEN_KEY = prefix + 'access_token';
                REFRESH_TOKEN_KEY = prefix + 'refresh_token';
                PKCE_VERIFIER_KEY = prefix + 'pkce_verifier';
                AUTH_LOG_KEY = prefix + 'auth_log';
            }

            logAuthEvent('init_start', { hasCallbackCode: new URLSearchParams(window.location.search).has('code') });

            this.notify();

            // Check for OAuth callback (code in query params)
            const params = new URLSearchParams(window.location.search);
            const code = params.get('code');

            if (code) {
                const state = params.get('state');
                const returnPath = state ? decodeURIComponent(state) : '/';
                await this._exchangeCode(code);
                window.history.replaceState({}, document.title, returnPath);
            } else {
                const token = await this._storage.get(ACCESS_TOKEN_KEY);
                if (token) {
                    this.isAuthenticated = true;
                    try {
                        // getAccessToken transparently refreshes if needed.
                        const fresh = await this.getAccessToken();
                        await this._fetchUser(fresh);
                    } catch (err) {
                        logAuthEvent('init_signout_after_failed_refresh', { message: err && err.message });
                        await this._clearTokens();
                        this.isAuthenticated = false;
                    }
                } else {
                    logAuthEvent('init_no_access_token');
                }
            }

            this.loading = false;
            this.notify();
        } catch (error) {
            console.error('Auth initialization error:', error);
            this.error = error.message;
            this.loading = false;
            this.notify();
        }
    },

    /**
     * Set a custom function to handle opening the OAuth authorize URL.
     * When set, login() calls this instead of redirecting the page.
     * @param {((authorizeUrl: string, redirectUri: string) => Promise<{code: string, state: string}>)|null} opener
     */
    setLoginOpener(opener) {
        this._loginOpener = opener;
    },

    async login() {
        if (!this._config) {
            throw new Error('Auth not initialized');
        }

        const { verifier, challenge } = await generatePKCE();
        await this._storage.set(PKCE_VERIFIER_KEY, verifier);

        const redirectUri = this._loginRedirectUri;
        const state = encodeURIComponent(window.location.pathname);
        const authorizeUrl = `${this._config.d3AuthFrontendURL}/oauth/${this._config.d3AuthTenantSlug}/authorize` +
            `?client_id=${encodeURIComponent(this._config.d3AuthClientID)}` +
            `&redirect_uri=${encodeURIComponent(redirectUri)}` +
            `&response_type=code` +
            `&audience=${encodeURIComponent(this._config.d3AuthAudience)}` +
            `&code_challenge=${encodeURIComponent(challenge)}` +
            `&code_challenge_method=S256` +
            `&state=${state}`;

        if (this._loginOpener) {
            const result = await this._loginOpener(authorizeUrl, redirectUri);
            await this._exchangeCode(result.code);
            this.loading = false;
            this.notify();
            // Navigate to the path the user was on when they started the login flow
            const returnPath = result.state ? decodeURIComponent(result.state) : '/';
            if (window.location.pathname !== returnPath) {
                window.history.pushState({}, '', returnPath);
                window.dispatchEvent(new PopStateEvent('popstate'));
            }
            return;
        }

        window.location.href = authorizeUrl;
    },

    /**
     * Exchange an authorization code for tokens. Public wrapper for use
     * by custom login openers that handle the code extraction themselves.
     * @param {string} code
     */
    async exchangeCode(code) {
        await this._exchangeCode(code);
        this.loading = false;
        this.notify();
    },

    /**
     * Set a custom function to handle opening the d3-auth logout URL.
     * When set, logout() calls this instead of redirecting the page.
     * @param {((logoutUrl: string, returnTo: string) => Promise<void>)|null} opener
     */
    setLogoutOpener(opener) {
        this._logoutOpener = opener;
    },

    async logout() {
        await this._clearTokens();
        this.isAuthenticated = false;
        this.user = null;
        this.notify();

        const returnTo = this._logoutReturnTo;
        const clientId = encodeURIComponent(this._config.d3AuthClientID);
        const logoutUrl = `${this._config.d3AuthBaseURL}/oauth/${this._config.d3AuthTenantSlug}/logout?client_id=${clientId}&returnTo=${encodeURIComponent(returnTo)}`;

        if (this._logoutOpener) {
            await this._logoutOpener(logoutUrl, returnTo);
            return;
        }

        window.location.href = logoutUrl;
    },

    async getAccessToken() {
        const token = await this._storage.get(ACCESS_TOKEN_KEY);
        if (!token) {
            throw new Error('Not authenticated');
        }
        // Refresh if any of:
        //   1. token is expired or within 60s of expiring (the usual case)
        //   2. token was issued more than 24h ago, regardless of its `exp`.
        // Rule (2) decouples the refresh cadence from per-API access-token
        // lifetime so the refresh-token idle clock gets reset at least daily.
        // Without it, a service that mints long-lived access tokens (e.g. 24h)
        // would let the refresh token edge up against its idle TTL whenever
        // the user came back near token expiry — exactly the failure that
        // logged users out of rpgish after a day of inactivity.
        const claims = decodeJwtPayload(token);
        const expiresAt = typeof claims.exp === 'number' ? claims.exp * 1000 : 0;
        const issuedAt = typeof claims.iat === 'number' ? claims.iat * 1000 : 0;
        const MAX_AGE_MS = 24 * 60 * 60 * 1000;
        const nearExpiry = expiresAt && expiresAt - Date.now() < 60_000;
        const tooOld = issuedAt && Date.now() - issuedAt > MAX_AGE_MS;
        if (nearExpiry || tooOld) {
            return await this._refreshTokens();
        }
        return token;
    },

    async _refreshTokens() {
        // Serialize concurrent refresh requests so N simultaneous API calls
        // don't each POST to /token. First caller does the refresh; others
        // await the same promise and get the same new token.
        if (this._refreshPromise) return this._refreshPromise;
        this._refreshPromise = (async () => {
            const refreshToken = await this._storage.get(REFRESH_TOKEN_KEY);
            if (!refreshToken) {
                logAuthEvent('refresh_aborted_no_refresh_token');
                await this._clearTokens();
                this.isAuthenticated = false;
                this.user = null;
                this.notify();
                throw new Error('Not authenticated');
            }
            logAuthEvent('refresh_attempt');
            let response;
            try {
                response = await fetch(`${this._config.d3AuthBaseURL}/oauth/${this._config.d3AuthTenantSlug}/token`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        grant_type: 'refresh_token',
                        refresh_token: refreshToken,
                        client_id: this._config.d3AuthClientID,
                    }),
                });
            } catch (err) {
                logAuthEvent('refresh_network_error', { message: err && err.message });
                await this._clearTokens();
                this.isAuthenticated = false;
                this.user = null;
                this.notify();
                throw new Error('Token refresh failed');
            }
            if (!response.ok) {
                // Refresh failed — tokens are no longer valid. Sign the user out.
                let body = '';
                try { body = (await response.text()).slice(0, 200); } catch { /* ignore */ }
                logAuthEvent('refresh_failed', { status: response.status, body });
                await this._clearTokens();
                this.isAuthenticated = false;
                this.user = null;
                this.notify();
                throw new Error('Token refresh failed');
            }
            const data = await response.json();
            await this._storage.set(ACCESS_TOKEN_KEY, data.access_token);
            if (data.refresh_token) {
                await this._storage.set(REFRESH_TOKEN_KEY, data.refresh_token);
            }
            const claims = decodeJwtPayload(data.access_token);
            logAuthEvent('refresh_success', { rotated: !!data.refresh_token, exp: claims.exp || null });
            return data.access_token;
        })();
        try {
            return await this._refreshPromise;
        } finally {
            this._refreshPromise = null;
        }
    },

    async _exchangeCode(code) {
        const verifier = await this._storage.get(PKCE_VERIFIER_KEY);
        await this._storage.remove(PKCE_VERIFIER_KEY);
        if (!verifier) {
            throw new Error('PKCE verifier not found — login flow may have been interrupted');
        }

        const redirectUri = this._loginRedirectUri;
        const response = await fetch(`${this._config.d3AuthBaseURL}/oauth/${this._config.d3AuthTenantSlug}/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                grant_type: 'authorization_code',
                code,
                redirect_uri: redirectUri,
                client_id: this._config.d3AuthClientID,
                code_verifier: verifier,
            }),
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.error_description || data.error || 'Token exchange failed');
        }

        const data = await response.json();
        await this._storage.set(ACCESS_TOKEN_KEY, data.access_token);
        if (data.refresh_token) {
            await this._storage.set(REFRESH_TOKEN_KEY, data.refresh_token);
        }

        this.isAuthenticated = true;
        await this._fetchUser(data.access_token);
    },

    async _fetchUser(token) {
        const response = await fetch(`${this._config.d3AuthBaseURL}/oauth/${this._config.d3AuthTenantSlug}/userinfo`, {
            headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!response.ok) {
            throw new Error('Failed to fetch user info');
        }

        const userInfo = await response.json();
        const claims = decodeJwtPayload(token);
        this.user = {
            name: userInfo.name || userInfo.username || userInfo.email,
            email: userInfo.email,
            sub: userInfo.sub,
            picture: userInfo.picture || null,
            permissions: (typeof claims.permissions === 'object' && !Array.isArray(claims.permissions))
                ? (claims.permissions[this._config.d3AuthAudience] || [])
                : (claims.permissions || []),
        };
    },

    async _clearTokens() {
        await this._storage.remove(ACCESS_TOKEN_KEY);
        await this._storage.remove(REFRESH_TOKEN_KEY);
    },
};
